import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  User, MapPin, GraduationCap, Briefcase, Globe, 
  ShieldCheck, Edit3, Camera, Trash2, X, Save, 
  FileText, Flag, Mail, Phone
} from 'lucide-react';
import './CandidateProfile.css';
import defaultProfilePic from '../../assets/default-profile.png'; 

const CandidateProfile = () => {
  const [profileData, setProfileData] = useState({});
  const [profilePicture, setProfilePicture] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const fileInputRef = useRef(null); 
  const [showOptions, setShowOptions] = useState(false);

  useEffect(() => {
    const fetchProfileData = async () => {
      const userId = localStorage.getItem('userId');
      if (!userId) return;
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/candidate/profile/${userId}`);
        const data = response.data;
        setProfileData(data);
        setOriginalData(data);
        
        // --- GCS URL HANDLING (PRESERVED) ---
        if (data.profilePicture) {
          if (data.profilePicture.startsWith('http')) {
            setProfilePicture(data.profilePicture);
          } else {
            setProfilePicture(`${process.env.REACT_APP_API_BASE_URL}/${data.profilePicture}`);
          }
        }
      } catch (error) {
        console.error('Failed to fetch profile data:', error);
      }
    };
    fetchProfileData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault(); 
    const userId = localStorage.getItem('userId');
    try {
      await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/candidate/update-profile/${userId}`, profileData);
      alert('Profile updated successfully!');
      setIsEditing(false);
      setOriginalData(profileData);
    } catch (error) {
      alert('Failed to update profile.');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setProfileData(originalData);
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('profilePicture', file);
    const userId = localStorage.getItem('userId');
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/candidate/update-profile-pic/${userId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProfilePicture(response.data.profilePicture); 
      setShowOptions(false); 
    } catch (error) {
      alert('Upload failed.');
    }
  };

  const renderField = (name, value, isPermanent = false) => {
      if (!isEditing || isPermanent) return <span className="info-value">{value || 'N/A'}</span>;
      return <input type="text" name={name} value={value || ''} onChange={handleChange} className="info-input-field" />;
  };

  const renderSelectField = (name, value, options) => {
      const displayValue = options.find(opt => String(opt.value) === String(value))?.label || 'N/A';
      if (!isEditing) return <span className="info-value">{displayValue}</span>;
      return (
          <select name={name} value={value} onChange={handleChange} className="info-input-field">
              {options.map(opt => <option key={opt.label} value={opt.value}>{opt.label}</option>)}
          </select>
      );
  };

  const booleanOptions = [ { label: 'Yes', value: true }, { label: 'No', value: false } ];

  return (
    <div id="candidate-profile-page">
      <div className="profile-sidebar-card">
        <div className="avatar-wrapper">
          <img src={profilePicture || defaultProfilePic} alt="Profile" />
          <div className="avatar-edit-trigger" onClick={() => setShowOptions(!showOptions)}>
              <Camera size={16} />
          </div>
          {showOptions && (
            <div className="avatar-popover">
              <button onClick={() => fileInputRef.current.click()}><Camera size={14} /> Update</button>
              <button className="text-danger" onClick={() => setProfilePicture(defaultProfilePic)}><Trash2 size={14} /> Remove</button>
            </div>
          )}
        </div>
        <h3 className="candidate-display-name">{profileData.fullName || 'Candidate Name'}</h3>
        <div className="candidate-badge">
            <ShieldCheck size={14} />
            <span>{profileData.rollNo || 'ID-N/A'}</span>
        </div>
        {/* REMOVED "PERSONAL PROFILE" TEXT HERE AS REQUESTED */}
      </div>

      <div className="profile-main-content">
        <div className="details-card">
          <div className="details-card-header">
            <h3 className="section-main-label">PROFILE SUMMARY</h3>
            {!isEditing ? (
              <button className="action-btn-outline" onClick={() => setIsEditing(true)}>
                <Edit3 size={14} /> EDIT PROFILE
              </button>
            ) : (
              /* FIXED SAVE/CANCEL ALIGNMENT */
              <div className="profile-action-group">
                <button onClick={handleSave} className="action-btn-save"><Save size={14} /> SAVE</button>
                <button onClick={handleCancel} className="action-btn-cancel"><X size={14} /> CANCEL</button>
              </div>
            )}
          </div>

          <div className="profile-form-grid">
            <div className="form-section">
              <div className="cp-section-header-row"><User size={18} /><h4>PERSONAL INFORMATION</h4></div>
              <div className="field-row"><span>Full Name:</span> {renderField('fullName', profileData.fullName, true)}</div>
              <div className="field-row"><span>Email:</span> {renderField('personalEmail', profileData.personalEmail, true)}</div>
              <div className="field-row"><span>Phone:</span> {renderField('phoneNumber', profileData.phoneNumber)}</div>
              <div className="field-row"><span>Citizenship:</span> {renderField('citizenship', profileData.citizenship)}</div>
            </div>

            <div className="form-section">
              <div className="cp-section-header-row"><MapPin size={18} /><h4>ADDRESS DETAILS</h4></div>
              <div className="field-row"><span>City:</span> {renderField('permanentCity', profileData.permanentCity)}</div>
              <div className="field-row"><span>State:</span> {renderField('permanentState', profileData.permanentState)}</div>
              <div className="field-row"><span>Country:</span> {renderField('permanentCountry', profileData.permanentCountry)}</div>
            </div>

            <div className="form-section">
              <div className="cp-section-header-row"><GraduationCap size={18} /><h4>EDUCATION</h4></div>
              <div className="field-row"><span>College:</span> {renderField('collegeName', profileData.collegeName, true)}</div>
              <div className="field-row"><span>CGPA:</span> {renderField('currentCGPA', profileData.currentCGPA)}</div>
              <div className="field-row"><span>Board:</span> {renderField('tenthBoard', profileData.tenthBoard)}</div>
            </div>

            <div className="form-section">
              <div className="cp-section-header-row"><Briefcase size={18} /><h4>EXPERIENCE</h4></div>
              <div className="field-row"><span>Total Exp:</span> {renderField('totalExperience', profileData.totalExperience)}</div>
              <div className="field-row"><span>Current CTC:</span> {renderField('currentCTC', profileData.currentCTC)}</div>
              <div className="field-row"><span>Expected CTC:</span> {renderField('expectedCTC', profileData.expectedCTC)}</div>
            </div>

            <div className="form-section full-span">
              <div className="cp-section-header-row"><Globe size={18} /><h4>LINKS & DOCUMENTS</h4></div>
              <div className="field-row"><span>Skills:</span> {renderField('skills', profileData.skills)}</div>
              <div className="field-row"><span>LinkedIn:</span> {renderField('linkedinURL', profileData.linkedinURL)}</div>
              <div className="field-row">
                <span>Resume:</span>
                {profileData.resumeFile ? (
                    <a href={profileData.resumeFile} target="_blank" rel="noreferrer" className="doc-link">
                        <FileText size={14} /> VIEW DOCUMENT
                    </a>
                ) : <span className="text-muted">Not uploaded</span>}
              </div>
            </div>
            
            <div className="form-section full-span">
              <div className="cp-section-header-row"><Flag size={18} /><h4>JOB STATUS</h4></div>
              <div className="field-row"><span>Serving Notice:</span> {renderSelectField('isServingNotice', profileData.isServingNotice, booleanOptions)}</div>
              <div className="field-row"><span>Notice Period:</span> {renderField('noticePeriod', profileData.noticePeriod)}</div>
              <div className="field-row"><span>Abroad Interest:</span> {renderSelectField('isAbroadOpportunity', profileData.isAbroadOpportunity, booleanOptions)}</div>
            </div>
          </div>
        </div>
      </div>
      <input type="file" ref={fileInputRef} onChange={handleProfilePictureUpload} hidden />
    </div>
  );
};
export default CandidateProfile;